import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Box, Typography, Button, Collapse } from '@mui/material';
import { motion } from 'framer-motion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut', delay } },
});

const Policies = () => {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    // Handle hash navigation on load and when hash changes
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
      setActiveSection(id);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  // Desktop only: highlight nav based on scroll position
  const observerRef = useRef(null);
  useEffect(() => {
    const isDesktop = () => window.innerWidth >= 600;
    if (!isDesktop()) return;

    const sectionIds = ['returns', 'durability', 'safety', 'liability'];
    const elements = sectionIds.map((id) => document.getElementById(id)).filter(Boolean);
    if (elements.length === 0) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (!isDesktop()) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-200px 0px -50% 0px', threshold: 0 }
    );

    elements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  const sections = [
    { id: 'returns', title: 'Returns & Refunds' },
    { id: 'durability', title: 'Durability' },
    { id: 'safety', title: 'Safety and Recommended Use' },
    { id: 'liability', title: 'Limits of Liability/Terms of Sale' }
  ];

  const scrollToSection = (id) => {
    setActiveSection(id);
    // Small delay to let mobile Collapse animation finish before scrolling
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 350);
  };

  return (
    <Container sx={{ p: 0, maxWidth: '100% !important' }}>
      {/* Hero Header */}
      <Box
        component={motion.section}
        {...fadeUp(0)}
        sx={{
          position: 'relative',
          py: { xs: 10, md: 14 },
          px: { xs: 2, sm: 4, md: 6 },
          background: 'var(--hero-background-gradient)',
          color: 'white',
          overflow: 'hidden',
        }}
      >
        {/* Soft blobs */}
        <Box
          sx={{
            position: 'absolute',
            top: '15%',
            right: -80,
            width: 320,
            height: 320,
            bgcolor: 'var(--hero-blur-bg)',
            borderRadius: '50%',
            filter: 'blur(60px)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '10%',
            left: -80,
            width: 280,
            height: 280,
            bgcolor: 'var(--hero-blur-bg)',
            borderRadius: '50%',
            filter: 'blur(60px)',
          }}
        />

        <Box sx={{ maxWidth: 1200, mx: 'auto', textAlign: 'center', position: 'relative' }}>
          <Typography variant="h2" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
            Policies
          </Typography>
          <Typography variant="h6" sx={{ mt: 2, opacity: 0.95, maxWidth: 820, mx: 'auto' }}>
            Our commitment to transparency and customer satisfaction
          </Typography>
        </Box>
      </Box>

      {/* Quick Navigation Menu */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut', delay: 0.05 } }}
        sx={{
          position: 'sticky',
          top: { xs: 60, sm: 68 },
          zIndex: 9,
          background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.95), rgba(37, 99, 235, 0.95))',
          backdropFilter: 'blur(16px)',
          boxShadow: '0 8px 32px rgba(37, 99, 235, 0.25)',
          py: { xs: 2, sm: 2.5 },
          px: { xs: 1.5, sm: 4 },
        }}
      >
        {/* Desktop: pill container */}
        <Box
          component="nav"
          sx={{
            display: { xs: 'none', sm: 'flex' },
            flexWrap: 'wrap',
            gap: 1.5,
            justifyContent: 'center',
            alignItems: 'center',
            maxWidth: 900,
            mx: 'auto',
            px: 1.5,
            py: 1,
            borderRadius: 9999,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            backdropFilter: 'blur(8px)',
          }}
        >
          {sections.map((section) => (
            <Button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              disableElevation
              sx={{
                px: 3,
                py: 1,
                borderRadius: 9999,
                fontSize: '0.95rem',
                fontWeight: 700,
                textTransform: 'none',
                border: 'none',
                whiteSpace: 'nowrap',
                color: activeSection === section.id ? '#1e40af !important' : 'white !important',
                backgroundColor: activeSection === section.id ? 'rgba(255, 255, 255, 0.95) !important' : 'transparent !important',
                boxShadow: activeSection === section.id ? '0 4px 16px rgba(0, 0, 0, 0.15)' : 'none',
                '&:hover': {
                  backgroundColor: activeSection === section.id
                    ? 'rgba(255, 255, 255, 1) !important'
                    : 'rgba(255, 255, 255, 0.18) !important',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {section.title}
            </Button>
          ))}
        </Box>

        {/* Mobile: expandable nav */}
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            onKeyDown={(e) => e.key === 'Enter' && setMobileNavOpen(!mobileNavOpen)}
            sx={{
              py: 1.25,
              px: 2,
              borderRadius: 2,
              fontSize: '0.9rem',
              fontWeight: 700,
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.22)',
              },
              transition: 'background-color 0.2s ease',
            }}
          >
            Jump to Section
            <ExpandMoreIcon
              sx={{
                color: 'white',
                transform: mobileNavOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
              }}
            />
          </Box>
          <Collapse in={mobileNavOpen} sx={{ '& .MuiCollapse-wrapperInner': { backgroundColor: 'transparent' } }}>
            <Box
              component="nav"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mt: 1,
                backgroundColor: 'transparent',
              }}
            >
              {sections.map((section) => (
                <Box
                  role="button"
                  tabIndex={0}
                  key={section.id}
                  onClick={() => {
                    scrollToSection(section.id);
                    setMobileNavOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      scrollToSection(section.id);
                      setMobileNavOpen(false);
                    }
                  }}
                  sx={{
                    py: 1.5,
                    pl: 2.5,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: 'white',
                    cursor: 'pointer',
                    userSelect: 'none',
                    backgroundColor: 'transparent',
                    borderLeft: activeSection === section.id ? '3px solid white' : '3px solid transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {section.title}
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      </Box>

      {/* Content */}
      <Box
        component={motion.section}
        {...fadeUp(0.1)}
        sx={{
          maxWidth: 1100,
          mx: 'auto',
          px: { xs: 2, sm: 4 },
          py: { xs: 6, md: 10 },
        }}
      >
        {/* Returns & Refunds Section */}
        <Box
          id="returns"
          component="section"
          sx={{
            mb: 8,
            scrollMarginTop: '180px',
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            backgroundImage: 'var(--card-background-gradient)',
            background: 'var(--modal-background)',
            boxShadow: 'var(--card-shadow)',
            border: '1px solid var(--card-border)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 2 }}>
            Returns & Refunds
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 2, fontStyle: 'italic' }}>
            (Direct & Amazon FBM Orders)
          </Typography>
          
            <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
              Products purchased directly from Amaze Puzzles, Inc., including Amazon FBM orders, may be returned within 45 days of delivery.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
              Eligibility
            </Typography>
            <Typography sx={{ color: 'var(--text-secondary)', mb: 1 }}>Returned items must:</Typography>
            <Box component="ul" sx={{ color: 'var(--text-secondary)', mb: 3, pl: 4 }}>
              <li>Include all original components</li>
              <li>Be free of excessive damage beyond normal classroom or personal use</li>
              <li>Be securely packaged for return shipment</li>
            </Box>
            <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
              Returns with missing components or structural damage may be subject to partial refund or denial.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
              Preference Returns (Change of Mind)
            </Typography>
            <Typography sx={{ color: 'var(--text-secondary)', mb: 1 }}>If returning because the product does not meet your needs:</Typography>
            <Box component="ul" sx={{ color: 'var(--text-secondary)', mb: 3, pl: 4 }}>
              <li>Customer is responsible for return shipping.</li>
              <li>Refund will be issued after inspection of the returned product.</li>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
              Defective Items
            </Typography>
            <Typography sx={{ color: 'var(--text-secondary)', mb: 1 }}>If the product arrives defective:</Typography>
            <Box component="ul" sx={{ color: 'var(--text-secondary)', mb: 3, pl: 4 }}>
              <li>Amaze Puzzles will cover return shipping.</li>
              <li>Customer may choose replacement or full refund.</li>
            </Box>

            <Typography sx={{ color: 'var(--text-secondary)', mb: 1 }}>
              Refunds are processed within 5–7 business days after inspection and issued to the original payment method.
            </Typography>
            <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>Proof of purchase may be required.</Typography>

            <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
              Institutional Addendum:
            </Typography>
            <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
              Products purchased through institutional purchase orders are eligible for return or replacement in cases of manufacturing defect or shipping damage only. Preference-based returns are not accepted for institutional bulk purchases.
            </Typography>

          </Box>

        {/* Durability Section */}
        <Box
          id="durability"
          component="section"
          sx={{
            mb: 8,
            scrollMarginTop: '180px',
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            backgroundImage: 'var(--card-background-gradient)',
            background: 'var(--modal-background)',
            boxShadow: 'var(--card-shadow)',
            border: '1px solid var(--card-border)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 3 }}>
            Durability Commitment
          </Typography>
          
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
            Amaze Puzzles trays are manufactured and individually inspected prior to shipment. We stand behind the structural integrity of each originally purchased tray.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            Manufacturing Defects (45 Days)
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 1 }}>Manufacturing defects are covered for 45 days from the delivery date.</Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 1 }}>
            A manufacturing defect is defined as structural separation or material failure not caused by misuse, heat exposure, modification, accidental damage, or improper storage.
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 1 }}>
            Due to our quality control process, manufacturing defects are expected to be immediately detectable upon use.
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>Proof of purchase is required.</Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
            If a manufacturing defect is confirmed within this window, Amaze Puzzles will provide a replacement tray or full refund at our discretion. Return shipping for confirmed defects is covered by Amaze Puzzles.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            One-Time Courtesy Tray Replacement
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 1 }}>
            We offer a one-time complimentary tray replacement per originally purchased tray, subject to the following conditions:
          </Typography>
          <Box component="ul" sx={{ color: 'var(--text-secondary)', mb: 3, pl: 4 }}>
            <li>Applies to tray only (puzzle pieces are not covered).</li>
            <li>Valid for the original purchaser only.</li>
            <li>Proof of purchase is required.</li>
            <li>Clear photographs of the damaged tray are required.</li>
            <li>Heat-related damage is not covered.</li>
            <li>Replacement tray color may vary.</li>
            <li>Replacement shipping is covered by Amaze Puzzles.</li>
          </Box>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
            This courtesy replacement applies once per originally purchased tray. Replacement trays obtained through prior warranty or courtesy replacement are not eligible for additional courtesy replacement coverage.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            Exclusions
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 1 }}>The Durability Commitment does not cover damage resulting from:</Typography>
          <Box component="ul" sx={{ color: 'var(--text-secondary)', mb: 3, pl: 4 }}>
            <li>Heat exposure (including vehicles or high-temperature environments)</li>
            <li>Deliberate damage</li>
            <li>Excessive force</li>
            <li>Modification or alteration</li>
            <li>Use of solvents or chemical cleaners</li>
            <li>Improper storage</li>
          </Box>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>Exceptions may be made at Amaze Puzzles' discretion.</Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            Scope
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
            This Durability Commitment applies to trays only and does not extend to puzzle pieces or accessory components.
          </Typography>

        </Box>

        {/* Safety Section */}
        <Box
          id="safety"
          component="section"
          sx={{
            mb: 8,
            scrollMarginTop: '180px',
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            backgroundImage: 'var(--card-background-gradient)',
            background: 'var(--modal-background)',
            boxShadow: 'var(--card-shadow)',
            border: '1px solid var(--card-border)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 3 }}>
            Safety & Recommended Use
          </Typography>
          
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
            Amaze Puzzles products are designed for tactile educational use in home, classroom, and rehabilitation settings.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            Supervision
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>Adult supervision is recommended where appropriate.</Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            Heat Exposure
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
            Avoid excessive heat, even for short periods of time. Do not leave the puzzle in vehicles or high-temperature environments. If it is too hot for you, it is too hot for the puzzle.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            Cleaning
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 1 }}>Clean using mild soap and water only. Disinfectant wipes may be used.</Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>Do not use solvents or harsh chemical cleaners.</Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            Moisture
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>Do not submerge the tray in water.</Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            Storage
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>Store flat in a cool, dry place.</Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            Modification
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
            Do not modify, alter, sand, drill, or structurally change any component of the product.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            Handling
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
            Puzzle pieces are solid, fully cured resin components intended for tactile handling. While fully cured resin is stable, the pieces are not intended for mouthing or chewing.
          </Typography>

        </Box>

        {/* Liability Section */}
        <Box
          id="liability"
          component="section"
          sx={{
            mb: 8,
            scrollMarginTop: '180px',
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            backgroundImage: 'var(--card-background-gradient)',
            background: 'var(--modal-background)',
            boxShadow: 'var(--card-shadow)',
            border: '1px solid var(--card-border)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 3 }}>
            Limitation of Liability & Terms of Sale
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            Agreement to Terms
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
            By purchasing a product from Amaze Puzzles, Inc., you agree to the policies and terms outlined on this website, including Returns & Refunds, Durability Commitment, and Safety & Recommended Use.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            Product Purpose
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 1 }}>
            Amaze Puzzles products are educational tools intended for tactile learning and engagement. They are not medical devices and are not intended to diagnose, treat, cure, or prevent any condition.
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>No guarantee of educational outcomes is made or implied.</Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            Limitation of Liability
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 1 }}>To the fullest extent permitted by law:</Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 1 }}>
            Amaze Puzzles' total liability arising from or related to any product shall be limited to repair, replacement, or refund of the purchase price paid for the product, at Amaze Puzzles' discretion.
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
            Amaze Puzzles shall not be liable for any incidental, indirect, special, or consequential damages, including but not limited to loss of profits, loss of use, loss of instructional time, or other secondary damages arising from use or misuse of the product.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            Governing Law
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
            These terms shall be governed by and construed in accordance with the laws of the State of Texas, without regard to conflict of law principles.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            Policy Modifications
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
            Amaze Puzzles reserves the right to modify these policies at any time. Updated policies will be posted on this website and apply to purchases made after the effective date of revision.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)', mb: 1.5 }}>
            Severability
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
            If any provision of these terms is found to be unenforceable or invalid, the remaining provisions shall remain in full force and effect.
          </Typography>

        </Box>
      </Box>
    </Container>
  );
};

export default Policies;
